import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../../components/ui/DataTable';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import PersonCell from '../../../components/ui/PersonCell';
import { usePeriodScopeDiffApi } from '../config/participants';
import { EXCLUSION_REASONS } from './PeriodParticipantsTab';
import PeriodNotice, { LIVE_PERIOD_STATUSES, periodNoticeKey } from './PeriodNotice';

const ADDABLE = ['active', 'evaluation'];
const EDITABLE = ['active', 'evaluation', 'calibration'];

// Spec 2026-10-05 §15/2 — kapsam tanımına uyup katılımcı olmayanlar (ekle) ve katılımcı olup
// tanıma artık uymayanlar (çıkar). Katılımcı tablosu dönemin kapsamını sabitlediği için
// sonradan departmana geçen kişi kendiliğinden girmez; fark burada görünür kılınır.
export default function PeriodScopeDiffTab({ period }) {
  const { t } = useTranslation();
  const verified = Boolean(period?.participantScopeVerifiedAt);
  const live = LIVE_PERIOD_STATUSES.includes(period?.status);
  // Fark yalnız devam eden + doğrulanmış dönemde anlamlı; diğerlerinde istek atılmaz
  // (doğrulanmamış dönemde uç 409 döner ve yakalayıcı hata tostu gösterirdi).
  const { fetching, data, add, exclude } = usePeriodScopeDiffApi(period?.id, live && verified);
  const [drawer, setDrawer] = useState({ mode: null, row: null });
  const noticeKey = live ? periodNoticeKey(period) : 'performance.period.scopeDiff.notLive';
  const canAdd = verified && ADDABLE.includes(period?.status);
  const canEdit = verified && EDITABLE.includes(period?.status);

  const columns = useMemo(() => [
    {
      name: 'employeeName',
      label: t('performance.period.participants.employee'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, meta) => <PersonCell name={v} profilePicture={data[meta.rowIndex]?.profilePicture} />,
      },
    },
    {
      name: 'diffType',
      label: t('performance.period.scopeDiff.diffType'),
      options: { filter: false, sort: false, customBodyRender: (v) => t(`performance.period.scopeDiff.types.${v}`, { defaultValue: v }) },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false,
        customBodyRender: (_v, meta) => {
          const row = data[meta.rowIndex];
          const actions = [];
          if (row?.diffType === 'not_participant' && canAdd) {
            actions.push({ type: 'apply', label: t('performance.period.participants.add'), onClick: () => setDrawer({ mode: 'add', row }) });
          }
          if (row?.diffType === 'no_longer_matching' && canEdit) {
            actions.push({ type: 'remove', label: t('performance.period.participants.exclude'), onClick: () => setDrawer({ mode: 'exclude', row }) });
          }
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [t, data, canAdd, canEdit]);

  const schema = drawer.mode === 'add'
    ? [{ name: 'note', label: t('performance.period.participants.addNote'), type: 'textarea', grid: 12, rows: 3, required: true, maxLength: 500 }]
    : [
      {
        name: 'reasonCode', label: t('performance.period.participants.reason'), type: 'select', grid: 12, required: true,
        options: EXCLUSION_REASONS.map((id) => ({ id, name: t(`performance.period.participants.reasons.${id}`) })), idKey: 'id', labelKey: 'name',
        defaultValue: 'role_change',
      },
      {
        name: 'note', label: t('performance.period.participants.note'), type: 'textarea', grid: 12, rows: 3, maxLength: 500,
        required: (formData) => formData.reasonCode === 'other',
      },
    ];

  const handleSave = useCallback(async (values) => (drawer.mode === 'add'
    ? add(drawer.row.employeeId, values.note)
    : exclude(drawer.row.participantId, values.reasonCode, values.note)), [drawer, add, exclude]);

  return (
    <>
      {noticeKey && <PeriodNotice>{t(noticeKey)}</PeriodNotice>}
      <DataTable
        title={t('performance.period.scopeDiff.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={data.length}
        pageIndex={0}
        pageSize={Math.max(data.length, 10)}
        canCreateNewRecord={false}
      />
      <GenericFormDrawer
        open={drawer.mode != null}
        onClose={() => setDrawer({ mode: null, row: null })}
        title={drawer.mode === 'add' ? t('performance.period.participants.addTitle') : t('performance.period.participants.excludeTitle')}
        schema={schema}
        initialValues={{}}
        onSave={handleSave}
      />
    </>
  );
}
